import type { ComponentProps } from "react";
import { t } from "ttag";

import type { PaymentMethod } from "@/shared/domains";
import type { SvgSlot } from "@/shared/ui";
import { Alert, RadioGroup, Skeleton } from "@/shared/ui";

interface Option {
  label: string;
  value: PaymentMethod["type"];
  icon: typeof SvgSlot;
}

type PaymentMethodTypeSelectorProps = ComponentProps<typeof RadioGroup.Root> & {
  options: Option[];
  loading?: boolean;
};

export const PaymentMethodTypeSelector = ({
  options,
  loading,
  value,
  ...props
}: PaymentMethodTypeSelectorProps) => {
  if (loading) {
    return (
      <div className="flex h-19.5 w-full items-center gap-2 sm:h-12.5">
        <Skeleton className="h-full flex-1" />
        <Skeleton className="h-full flex-1" />
      </div>
    );
  }

  if (!options.length) {
    return (
      <Alert type="error">{t`No payment method available. Please add one before proceeding.`}</Alert>
    );
  }

  return (
    <RadioGroup.Root
      isCard
      color="nature"
      className="flex flex-col items-stretch gap-2 sm:flex-row"
      value={value}
      {...props}
    >
      {options.map(({ value, label, icon: Icon }) => (
        <RadioGroup.Item key={value} value={value} className="flex-1">
          <div className="flex items-center gap-2">
            <Icon className="size-5" />
            <RadioGroup.Label>{label}</RadioGroup.Label>
          </div>
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  );
};
